/**
 * Perfis do cadastro de usuário, do maior para o menor nível de alçada. A ordem decide qual
 * perfil representa o usuário quando ele tem mais de um marcado — no cabeçalho aparece só o
 * primeiro desta lista, e é ele que ganha destaque entre as pílulas do menu de conta.
 *
 * Perfil que não estiver aqui (um criado depois, por exemplo) vai para o fim da fila em vez de
 * sumir: a lista é para ordenar, não para filtrar.
 */
const ROLE_PRECEDENCE = [
  'Administrador',
  'Diretoria',
  'Gerência',
  'Supervisor',
  'Operacional/Financeiro',
  'Contábil',
  'Cobrança',
  'Cadastro',
  'Operador',
  'Recepção',
  'Correspondentes',
  'Prospecção',
  'Marketing'
]

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

const PRECEDENCE_BY_NAME = ROLE_PRECEDENCE.reduce<Record<string, number>>(
  (map, name, index) => {
    map[normalize(name)] = index
    return map
  },
  {}
)

/**
 * Os perfis do usuário ordenados por alçada, sem repetição. O mesmo perfil pode estar marcado em
 * mais de uma empresa e ainda assim é um só para quem lê o cabeçalho.
 */
export function sortRolesByPrecedence(roles: string[]): string[] {
  const unique: string[] = []

  roles?.forEach(role => {
    if (role && !unique.some(item => normalize(item) === normalize(role))) {
      unique.push(role)
    }
  })

  return unique.sort((a, b) => {
    const rankA = PRECEDENCE_BY_NAME[normalize(a)] ?? ROLE_PRECEDENCE.length
    const rankB = PRECEDENCE_BY_NAME[normalize(b)] ?? ROLE_PRECEDENCE.length

    if (rankA !== rankB) {
      return rankA - rankB
    }

    return a.localeCompare(b, 'pt-BR')
  })
}

/**
 * Iniciais do nome, sempre em maiúsculas e sem acento: primeiro e último nome ("Douglas
 * Numeriano" = DN), ou uma letra só quando há um nome apenas.
 */
export function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)

  if (!parts.length) {
    return ''
  }

  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''

  return `${first}${last}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
}

/**
 * Nome curto para o cabeçalho: primeiro nome mais a inicial do sobrenome ("Douglas N.").
 */
export function getShortName(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)

  if (!parts.length) {
    return ''
  }

  if (parts.length === 1) {
    return parts[0]
  }

  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
}
